import { DecimalPipe, DatePipe } from '@angular/common';
import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { TableTransactionsService } from './table-transactions.service';
import { NgbDateStruct, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ExtratoService } from '../extrato/extrato.service';
import { ToastrService } from "ngx-toastr";
import { saveAs } from 'file-saver';
import { PdfService } from 'src/app/shared/services/pdf.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { AuthService } from 'src/app/shared/services/auth.service';


@Component({
  selector: 'app-table-transactions',
  templateUrl: './table-transactions.component.html',
  styleUrls: ['./table-transactions.component.scss'],
  providers: [TableTransactionsService, DecimalPipe, DatePipe]
})
export class TableTransactionsComponent {
  @ViewChild('transactionDetailsModal')
  private transactionDetailsTpl!: TemplateRef<any>;
  public originCurrency: string = 'BRL';
  public dataInitial!: NgbDateStruct;
  public dataFinal!: NgbDateStruct;
  public transactionType: string = '';
  public selectedStatus: string = '';
  public selectedOrigin: string = '';
  public selectedApplciation: string = '';
  public selectedWebhookStatus: string = '';
  public personName: string = '';
  public searchType: string = '';
  public page: number = 1;
  public pageSize: number = 20;
  public total: number = 0;
  public receiptTemplate: string = '';
  public bankProvider: Array<any> = [];
  public nameColumn: string = '';
  public account: any;
  public transactions: Array<any> = [];
  public selectedTransaction: any;
  public selectedCrypto: any = null;
  public canDownloadReceipt = false;
  public canResendWebhook = false;
  public isReprocessing = false;
  public tableRowForReceipt: any = null;
  public startDateIso?: string;
  public endDateIso?: string;
  public pad(n: number) { return String(n).padStart(2, '0'); }
  langChangeSub!: Subscription;
  public canResendRefund = false;
  public isRefunding = false;

  constructor(private extratoService: ExtratoService,
    private tableTransactionService: TableTransactionsService,
    private toastr: ToastrService,
    private pdfService: PdfService,
    private translate: TranslateService,
    private modal: NgbModal,
    private datePipe: DatePipe,
    private decimal: DecimalPipe,
    private auth: AuthService,) {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate());

    this.dataInitial = { year: currentDate.getFullYear(), month: currentDate.getMonth() + 1, day: currentDate.getDate() }
    this.dataFinal = { year: new Date().getFullYear(), month: new Date().getMonth() + 1, day: new Date().getDate() }
    this.selectedStatus = 'REALIZADO';
  }


  ngOnInit(): void {
    this.setTranslatedNameColumn();

    this.auth.getAccount().subscribe({
      next: (acc) => {
        this.originCurrency =
          (acc?.originCurrency || this.auth.getOriginCurrency() || 'BRL').toUpperCase();
      },
      error: () => {
        this.originCurrency = (this.auth.getOriginCurrency() || 'BRL').toUpperCase();
      }
    });

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.setTranslatedNameColumn();
    })
  }

  setTranslatedNameColumn(): void {
    this.translate.get(['common.license', 'common.split', 'common.reserve']).subscribe(t => {
      const raw = this.account?.nameColumn || '';

      if (raw.includes('LICENCIADO')) {
        this.nameColumn = t['common.license'];
      } else if (raw.includes('RESERVA')) {
        this.nameColumn = t['common.reserve'];
      } else {
        this.nameColumn = t['common.split'];
      }
    });
  }

  isValidPixDate(transactionDate: string): boolean {
    const targetDate = new Date('2025-02-05T00:00:00Z');
    const transactionDateObj = new Date(transactionDate);

    return transactionDateObj.getTime() >= targetDate.getTime();
  }

  isValidTefDate(transactionDate: string): boolean {
    const targetDate = new Date('2025-05-02T00:00:00Z');
    const transactionDateObj = new Date(transactionDate);

    return transactionDateObj.getTime() >= targetDate.getTime();
  }

  public getTransactions() {
    this.getAccount();

    let dateInitialFormat: string;
    let dateFinalFormat: string;

    if (this.startDateIso && this.endDateIso) {
      dateInitialFormat = this.startDateIso;
      dateFinalFormat = this.endDateIso;
    } else {
      const y1 = this.dataInitial.year, m1 = this.pad(this.dataInitial.month), d1 = this.pad(this.dataInitial.day);
      const y2 = this.dataFinal.year, m2 = this.pad(this.dataFinal.month), d2 = this.pad(this.dataFinal.day);
      dateInitialFormat = `${y1}-${m1}-${d1}T00:00:00`;
      dateFinalFormat = `${y2}-${m2}-${d2}T23:59:59`;
    }

    this.extratoService.getTransactionsExtrato(
      dateInitialFormat,
      dateFinalFormat,
      this.transactionType,
      this.selectedStatus,
      this.selectedOrigin,
      this.selectedApplciation,
      this.personName,
      this.page,
      this.pageSize,
      this.searchType,
      this.selectedWebhookStatus
    ).subscribe(value => {
      this.transactions = value.transactions;
      this.total = value.total;
    });
  }

  openDetails(row: any): void {
    this.tableRowForReceipt = row;

    this.canDownloadReceipt =
      row.tipo === 'Saque' &&
      (
        (row.origem === 'PIX' && this.isValidPixDate(row.data)) ||
        (row.origem === 'BOLETO' && this.isValidPixDate(row.data)) ||
        (row.origem === 'TEF' && this.isValidTefDate(row.data)) ||
        (row.origem === 'CRIPTO' && this.isValidTefDate(row.data))
      );

    this.canResendWebhook =
      ['REALIZADO', 'CANCELADO'].includes(row.status) &&
      this.isApiOrTef(row) &&
      !this.isBlockedOrigin(row);

    this.tableTransactionService.getTransactionDetail(row.codigo)
      .then(resp => {
        const detail = resp.transaction ?? resp;

        const history = Array.isArray(resp.webHookHistory)
          ? [...resp.webHookHistory].sort((a, b) =>
            new Date(b?.sendDate ?? 0).getTime() - new Date(a?.sendDate ?? 0).getTime()
          )
          : [];

        this.selectedTransaction = {
          ...detail,
          cryptoTransaction: resp.cryptoTransaction ?? null,
          wallet: resp.wallet ?? null,
          symbol: resp.symbol ?? null,
          webHookHistory: history,
          splitAccountIdDescription: resp.splitAccountIdDescription ?? null,
          splitAccountIdValue: resp.splitAccountIdValue ?? null,
          parentId: resp.parentId ?? null
        };

        this.canResendRefund = this.showRefundButton(this.selectedTransaction);

        this.modal.open(this.transactionDetailsTpl, {
          centered: true,
          backdrop: 'static',
          windowClass: 'tall-modal',
          scrollable: true,
          size: 'lg'
        });
      })
      .catch(() => this.toastr.error('Erro ao buscar detalhes'));
  }

  fmtDate(d: string) { return this.datePipe.transform(d, 'dd/MM/yyyy HH:mm:ss', 'UTC'); }
  fmtMoney(v: number) { return this.decimal.transform(v, '1.2-2', 'pt-BR'); }

  public getAccount() {
    this.tableTransactionService.getAccount()
      .then(value => {
        this.account = value;

        if (this.account?.nameColumn) {
          const raw = this.account.nameColumn;

          if (raw.includes('LICENCIADO')) {
            this.nameColumn = this.translate.instant('common.license');
          } else if (raw.includes('RESERVA')) {
            this.nameColumn = this.translate.instant('common.reserve');
          } else {
            this.nameColumn = this.translate.instant('common.split');
          }
        }
      })
      .catch(err => console.error('Error fetching account:', err));
  }

  formatStatusClass(status: string): string {
    return 'status-' + status.toLowerCase().replace(/ /g, '-');
  }

  pageChange() {
    this.getTransactions();
  }

  async resendWebhook(transaction: any) {
    if (this.isReprocessing) { return; }
    this.isReprocessing = true;

    this.toastr.info('Reenviando webhook…', '', { timeOut: 2000 });

    try {
      await this.tableTransactionService.resendWebhook([transaction.id]);
      this.toastr.success('Webhook reenviado com sucesso');
    } catch {
      this.toastr.error('Falha ao reenviar webhook');
    } finally {
      this.isReprocessing = false;
    }
  }

  webhookTooltip(tx: any): string {
    const val = (tx?.userNotificationWebHook ?? '').toString().trim();
    return `Notificação Webhook: ${val || '-'}`;
  }

  isApiOrTef(tx: any): boolean {
    const app = (tx?.aplicacao || tx?.application || '').toString().toUpperCase();
    return app === 'API' || app === 'TEF';
  }

  isWebOrTefWeb(tx: any): boolean {
    const app = (tx?.aplicacao || tx?.application || '').toString().toUpperCase();
    return app === 'WEB' || app === 'TEFWEB';
  }

  hasWebhookValue(tx: any): boolean {
    const v = tx?.userNotificationWebHook;
    return typeof v === 'string' && v.trim().length > 0;
  }


  async downloadReceipt(rowFromGrid: any) {
    this.toastr.info('Gerando comprovante…', '', {
      timeOut: 2000, positionClass: 'toast-top-right',
    });

    try {
      const payload = rowFromGrid;

      const response = await firstValueFrom(this.pdfService.reportWithdraw(payload));
      const bytes = atob(response);
      const buf = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);

      saveAs(new Blob([buf], { type: 'application/pdf' }), 'comprovante.pdf');

      this.toastr.success('Comprovante gerado com sucesso!', 'Sucesso', {
        timeOut: 3000, positionClass: 'toast-top-right',
      });
    } catch (err: any) {
      const msg = err?.error?.mensagem || err?.message || 'Falha ao gerar comprovante.';
      this.toastr.error(msg, 'Erro', {
        timeOut: 3000, positionClass: 'toast-top-right',
      });
    }
  }

  isBlockedOrigin(tx: any): boolean {
    const origin = (tx?.origem || tx?.origin || '')
      .toString()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    return (
      origin === 'TAXA BANCARIA' ||
      origin === 'COMISSAO' ||
      origin === 'REEMBOLSO'
    );
  }

  private showRefundButton(tx: any): boolean {
    if (!tx) return false;

    const status = tx.status;
    const type = tx.type;
    const origin = tx.origin;

    return status === 'EM PROCESSAMENTO'
      && type === 'DEPOSITO'
      && origin === 'PIX';
  }

  async resendRefund() {
    if (!this.selectedTransaction?.id || this.isRefunding) return;

    this.isRefunding = true;
    this.toastr.info('Reenviando reembolso…', '', { timeOut: 2000 });

    try {
      await this.tableTransactionService.resendRefund(this.selectedTransaction.id);
      this.toastr.success('Reembolso reenviado com sucesso');
    } catch {
      this.toastr.error('Falha ao reenviar reembolso');
    } finally {
      this.isRefunding = false;
    }
  }
}


